// page-notes-web/src/components/Modals/TagModal.jsx
import { useState, useEffect } from 'react';
import { useTags } from '../../contexts/TagsContext';
import Icon from '../common/Icon';
import './TagModal.css';
import './Modals.css';

const TAG_COLORS = [
  '#ffaa00', '#ff6b6b', '#51cf66', '#339af0', '#845ef7',
  '#f783ac', '#20c997', '#ffd43b', '#ff922b', '#868e96',
  '#e64980', '#be4bdb', '#7950f2', '#4c6ef5', '#228be6',
  '#15aabf', '#12b886', '#82c91e', '#fab005', '#fd7e14'
];

export default function TagModal({ isOpen, onClose, tag = null }) {
  const { createTag, updateTag } = useTags();

  const [name, setName] = useState('');
  const [color, setColor] = useState(TAG_COLORS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isEditing = !!tag;

  useEffect(() => {
    if (tag) {
      setName(tag.name || '');
      setColor(tag.color || TAG_COLORS[0]);
    } else {
      setName('');
      setColor(TAG_COLORS[0]);
    }
    setError(null);
  }, [tag, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Tag name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await updateTag(tag.id, { name: name.trim(), color });
      } else {
        await createTag({ name: name.trim(), color });
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save tag');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Tag' : 'Create Tag'}</h2>
          <button className="modal-close" onClick={onClose}><Icon name="X" /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error">{error}</div>}

            {/* Preview */}
            <div className="tag-preview">
              <span
                className="preview-tag"
                style={{
                  backgroundColor: `${color}22`,
                  borderColor: color,
                  color: color
                }}
              >
                {name || 'Tag Name'}
              </span>
            </div>

            {/* Name */}
            <div className="form-group">
              <label htmlFor="tagName">Name</label>
              <input
                id="tagName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter tag name"
                autoFocus
              />
            </div>

            {/* Color selector */}
            <div className="form-group">
              <label>Color</label>
              <div className="color-grid large">
                {TAG_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`color-option ${color === c ? 'selected' : ''}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Tag')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}